<template>
  <v-app>
    <v-container fluid class="pa-0">
      <v-row>
        <v-col cols="12">
          <v-card variant="outlined" class="mb-3">
            <v-card-title class="text-subtitle-1 bg-grey-lighten-4">
              Employee Daily Attendance Sheet
            </v-card-title>
            <v-card-text>
              <v-form @submit.prevent="fetchSheet">
                <v-row dense>
                  <v-col cols="12" md="3">
                    <v-date-input
                      v-model="filters.from_date"
                      label="From Date"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar"
                      hide-details="auto"
                      clearable
                      :max="filters.to_date"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-date-input
                      v-model="filters.to_date"
                      label="To Date"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar"
                      hide-details="auto"
                      clearable
                      :min="filters.from_date"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-autocomplete
                      v-model="filters.employee_id"
                      :items="employees"
                      :item-title="formatEmployeeLabel"
                      item-value="id"
                      label="Employee (Optional)"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      :loading="employeesLoading"
                      clearable
                      hide-details="auto"
                      placeholder="Search employee"
                    />
                  </v-col>
                  <v-col cols="12" md="2" class="d-flex gap-2">
                    <v-btn
                      color="primary"
                      variant="flat"
                      type="submit"
                      :loading="loading"
                      block
                    >
                      <v-icon size="18" class="me-2">mdi-magnify</v-icon>
                      Search
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card variant="outlined">
            <v-card-title class="text-subtitle-1 bg-grey-lighten-4 d-flex justify-space-between align-center">
              <span>Attendance Details</span>
              <v-btn size="small" variant="text" color="primary" @click="resetFilters">
                Reset Filters
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-progress-linear
                v-if="loading"
                indeterminate
                color="primary"
                class="mb-4"
              />

              <v-alert
                v-if="!loading && sheetData.length === 0"
                type="info"
                variant="tonal"
              >
                No attendance records found for the selected criteria.
              </v-alert>

              <div v-if="sheetData.length">
                <v-table density="comfortable" class="text-caption table-bordered">
                  <thead>
                    <tr>
                      <th># SL</th>
                      <th>Date</th>
                      <th>Employee</th>
                      <th>Department</th>
                      <th>Designation</th>
                      <th style="min-width: 150px;">Shift (In/Out)</th>
                      <th>Punch In</th>
                      <th>Punch Out</th>
                      <th style="text-align: center;">Working Hours</th>
                      <th style="text-align: center;">Late (min)</th>
                      <th style="text-align: center;">Early (min)</th>
                      <th style="text-align: center;">OT (hrs)</th>
                      <th>Status</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody >
                    <tr v-for="(row, index) in sheetData" :key="`${row.employee_code}-${row.date}-${index}`">
                      <td>{{ index + 1 }}</td>
                      <td>{{ formatDateView(row.date) }}</td>
                      <td>
                        <div class="font-weight-medium">{{ row.employee_name || '-' }}</div>
                        <div class="text-medium-emphasis">{{ row.employee_code || '-' }}</div>
                      </td>
                      <td>{{ row.department || '-' }}</td>
                      <td>{{ row.designation || '-' }}</td>
                      <td style="min-width: 150px;">
                        {{ formatTime(row.shift?.in_time) }} - {{ formatTime(row.shift?.out_time) }}
                      </td>
                      <td>{{ formatTime(row.punch_in) }}</td>
                      <td>{{ formatTime(row.punch_out) }}</td>
                      <td style="text-align: center;">{{ formatNumber(row.working_hours, 2) }}</td>
                      <td style="text-align: center;">{{ formatNumber(row.late_minutes) }}</td>
                      <td style="text-align: center;">{{ formatNumber(row.early_leaving_minutes) }}</td>
                      <td style="text-align: center;">{{ formatNumber(row.overtime_minutes / 60, 2) }}</td>
                      <td>
                        <v-chip
                          size="small"
                          :color="statusColor(row.status?.name)"
                          variant="text"
                        >
                          {{ row.status?.name || '-' }}
                        </v-chip>
                      </td>
                      <td>{{ row.message || '-' }}</td>
                    </tr>
                  </tbody>
                  <tfoot v-if="sheetData.length > 0">
                    <tr style="font-weight: bold; color: #000000;">
                      <td colspan="8" style="text-align: right; padding: 12px;">Total:</td>
                      <td style="text-align: center; padding: 12px;">{{ formatNumber(totals.working_hours, 2) }}</td>
                      <td style="text-align: center; padding: 12px;">{{ formatNumber(totals.late_minutes) }}</td>
                      <td style="text-align: center; padding: 12px;">{{ formatNumber(totals.early_leaving_minutes) }}</td>
                      <td style="text-align: center; padding: 12px;">{{ formatNumber(totals.overtime_hours, 2) }}</td>
                      <td colspan="2"></td>
                    </tr>
                  </tfoot>
                </v-table>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import { hrmService } from '@/services/hrmService'
import { attendanceService } from '@/services/attendanceService'
import formatDateView from '@/plugins/dateFormatView'

const employees = ref([])
const employeesLoading = ref(false)
const loading = ref(false)
const sheetData = ref([])

const filters = reactive({
  from_date: '',
  to_date: '',
  employee_id: null
})

const formatEmployeeLabel = item => {
  if (!item) return ''
  return `${item.name || ''}${item.employee_code ? ` - ${item.employee_code}` : ''}`
}

const normalizeDate = value => {
  if (!value) return ''
  if (value instanceof Date) {
    return value.toISOString().split('T')[0]
  }
  return value
}

const formatNumber = (value, fraction = 0) => {
  if (value === undefined || value === null || isNaN(value)) return '-'
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction
  })
}

const formatTime = (timeStr) => {
  if (!timeStr || timeStr === '-') return '-'
  
  // Handle time strings like "08:30:00" or "08:30"
  const timeParts = timeStr.split(':')
  if (timeParts.length < 2) return timeStr
  
  const hours = parseInt(timeParts[0], 10)
  const minutes = timeParts[1]
  
  if (isNaN(hours)) return timeStr
  
  const period = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
  
  return `${displayHours}:${minutes} ${period}`
}

// Computed totals
const totals = computed(() => {
  return sheetData.value.reduce(
    (acc, row) => {
      acc.working_hours += Number(row.working_hours) || 0
      acc.late_minutes += Number(row.late_minutes) || 0
      acc.early_leaving_minutes += Number(row.early_leaving_minutes) || 0
      acc.overtime_hours += (Number(row.overtime_minutes) || 0) / 60
      return acc
    },
    {
      working_hours: 0,
      late_minutes: 0,
      early_leaving_minutes: 0,
      overtime_hours: 0
    }
  )
})

const statusColor = status => {
  switch (status) {
    case 'Present':
      return 'success'
    case 'Late Check In':
    case 'Late Check In & Early Checkout':
      return 'warning'
    case 'Early Checkout':
      return 'purple'
    case 'Absent':
      return 'error'
    case 'Leave':
      return 'blue'
    case 'Weekend':
    case 'Holiday':
      return 'teal'
    default:
      return 'grey'
  }
}

const setDefaultDates = () => {
  const today = new Date()
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
  filters.from_date = normalizeDate(firstDay)
  filters.to_date = normalizeDate(today)
}

const loadEmployees = async () => {
  employeesLoading.value = true
  try {
    const response = await hrmService.getEmployees()
    employees.value = response.data || []
  } catch (error) {
    employees.value = []
    toast.error(error.message || 'Failed to load employees')
  } finally {
    employeesLoading.value = false
  }
}

const fetchSheet = async () => {
  if (!filters.from_date || !filters.to_date) {
    toast.warning('Please select both From Date and To Date')
    return
  }

  loading.value = true
  try {
    const params = {
      from_date: normalizeDate(filters.from_date),
      to_date: normalizeDate(filters.to_date),
      employee_id: filters.employee_id || undefined
    }

    const response = await attendanceService.getEmployeeDailyAttendanceSheet(params)
    if (response.success) {
      sheetData.value = response.data || []
    } else {
      sheetData.value = []
      toast.error(response.message || 'Failed to fetch attendance sheet')
    }
  } catch (error) {
    sheetData.value = []
    toast.error(error.response?.data?.message || error.message || 'Failed to fetch attendance sheet')
  } finally {
    loading.value = false
  }
}

const resetFilters = () => {
  setDefaultDates()
  filters.employee_id = null
  sheetData.value = []
}

onMounted(async () => {
  setDefaultDates()
  await loadEmployees()
})
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}

.table-bordered :deep(th),
.table-bordered :deep(td) {
  border: 1px solid #000000 !important;
}

.table-bordered :deep(table) {
  border-collapse: collapse;
}
</style>

